import axios, { AxiosError, type AxiosResponse } from 'axios';
import { useQuery } from 'react-query';

import { type TTransport } from '@/types/models';
import { type TBaseRequestPayload } from '@/types/request';
import { type TBaseResponse, type TErrorResponse } from '@/types/response';

type TGetTransportRequestPayload = TBaseRequestPayload & {
  trackingNumber: string;
  refetchInterval?: number | false;
};

const TRANSPORT_REFETCH_INTERVAL_MS = 60_000;

type TGetTransportResponse = TBaseResponse & {
  transport: TTransport;
};

const getTransport = async ({
  signal,
  trackingNumber,
}: TGetTransportRequestPayload) => {
  const res = await axios.get<TGetTransportResponse | TErrorResponse>(
    `/api/v1/track/${trackingNumber}`,
    { signal }
  );

  const payload = res.data;

  if (!payload?.success) {
    const errorPayload = payload as TErrorResponse;
    throw new AxiosError<TErrorResponse>(
      errorPayload?.error?.message ?? 'Transport not found',
      'ERR_BAD_REQUEST',
      undefined,
      undefined,
      res as AxiosResponse<TErrorResponse>
    );
  }

  return payload;
};

const useTransportQuery = ({
  trackingNumber,
  refetchInterval,
}: TGetTransportRequestPayload) => {
  const pollInterval = refetchInterval ?? TRANSPORT_REFETCH_INTERVAL_MS;

  return useQuery<TGetTransportResponse, AxiosError<TErrorResponse>>(
    ['transport', trackingNumber],
    ({ signal }) =>
      getTransport({
        signal,
        trackingNumber,
      }),
    {
      keepPreviousData: true,
      refetchInterval: pollInterval,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      retry: 0,
    }
  );
};

export { useTransportQuery };
